import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { Textarea } from "@/components/ui/textarea";
import { useEjercicio } from "@/context/EjercicioContext";

const STEPS = [
  {
    key: "cambio",
    titulo: "¿Qué cambio llevas tiempo posponiendo?",
    max: 500,
    vacio: "Empieza poniendo nombre a ese cambio.",
  },
  {
    key: "importancia",
    titulo: "¿Qué hace importante este cambio para ti ahora?",
    max: 500,
    vacio: "Completa este paso para continuar.",
  },
  {
    key: "primer_paso",
    titulo:
      "¿Cuál es el paso más pequeño que depende de ti y podrías dar en las próximas 72 horas?",
    max: 300,
    vacio: "Completa este paso para continuar.",
  },
];

export default function Claridad() {
  const navigate = useNavigate();
  const { data, update } = useEjercicio();
  const [i, setI] = useState(0);
  const [error, setError] = useState("");

  const step = STEPS[i];
  const value = data[step.key] || "";
  const progress = ((i + 1) / STEPS.length) * 100;

  const onChange = (e) => {
    setError("");
    update({ [step.key]: e.target.value.slice(0, step.max) });
  };

  const next = () => {
    if (!value.trim()) {
      setError(step.vacio);
      return;
    }
    if (i < STEPS.length - 1) {
      setI(i + 1);
    } else {
      navigate("/tu-paso");
    }
  };

  const back = () => {
    setError("");
    if (i > 0) setI(i - 1);
    else navigate("/");
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <main className="wrap-narrow py-10 sm:py-14">
        <button
          type="button"
          onClick={back}
          data-testid="claridad-back"
          className="mb-8 inline-flex items-center gap-2 text-base text-[#626B79] hover:text-[#275FBE]"
        >
          <ArrowLeft size={18} /> Atrás
        </button>

        <p className="eyebrow mb-4">No necesitas resolver tu vida entera hoy.</p>

        {/* Progreso */}
        <div className="mb-10">
          <div className="flex items-center justify-between text-sm text-[#626B79]">
            <span>Paso {i + 1} de {STEPS.length}</span>
            <span className="inline-flex items-center gap-1.5 text-[#1E6A45]">
              <Check size={15} /> Guardado
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#E6EEFA]">
            <motion.div
              className="h-full rounded-full bg-[#275FBE]"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.25 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step.key}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25 }}
          >
            <label
              htmlFor="claridad-input"
              className="font-display block text-2xl text-[#102B5B] sm:text-3xl"
              style={{ fontWeight: 600 }}
            >
              {step.titulo}
            </label>
            <p className="mt-3 text-base text-[#626B79]">
              No busques la respuesta perfecta. Escribe lo que ahora mismo sea verdad para ti.
            </p>
            <Textarea
              id="claridad-input"
              data-testid="claridad-input"
              value={value}
              onChange={onChange}
              rows={5}
              placeholder="Escribe aquí…"
              className="mt-5 min-h-[140px] rounded-2xl border-[#D6DAE0] text-base focus-visible:ring-[#275FBE]"
              aria-invalid={!!error}
            />
            <div className="mt-2 flex items-center justify-between">
              {error ? (
                <span className="field-error" data-testid="claridad-error">
                  ⚠ {error}
                </span>
              ) : (
                <span />
              )}
              <span className="text-sm text-[#626B79]">
                {value.length}/{step.max}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>

        <button
          type="button"
          onClick={next}
          data-testid="claridad-next"
          className="mt-8 w-full rounded-full bg-[#275FBE] py-4 text-base font-semibold text-white transition-transform duration-150 hover:scale-[1.01] active:scale-95 sm:w-auto sm:px-10"
        >
          {i < STEPS.length - 1 ? "Seguir" : "Definir mi paso"}
        </button>
      </main>
    </div>
  );
}
